import createCachedSelector from 're-reselect';

import { ReduxState } from 'app/redux/types';

import { BettingEntitiesSelectors } from 'app/lib/betting/betting-entities';
import { getPrefixBySlotType } from 'app/modules/Navigation/utils';

import { BreadcrumbType } from '../constants';

// ---

type EntityIdKey = 'sportId' | 'tournamentUnionId' | 'tournamentStageId' | 'tournamentId';
type Entities =
  | BettingEntitiesSelectors['sports']
  | BettingEntitiesSelectors['tournamentStages']
  | BettingEntitiesSelectors['tournaments']
  | BettingEntitiesSelectors['tournamentUnions'];

export default (
  matches: BettingEntitiesSelectors['matches'],
  sports: BettingEntitiesSelectors['sports'],
  tournamentStages: BettingEntitiesSelectors['tournamentStages'],
  tournaments: BettingEntitiesSelectors['tournaments'],
  tournamentUnions: BettingEntitiesSelectors['tournamentUnions'],
) => {
  function getEntityName(
    state: ReduxState,
    matchId: number,
    entities: Entities,
    entityIdKey: EntityIdKey,
  ) {
    const entityId = matches(state).get(matchId)?.[entityIdKey];

    if (typeof entityId !== 'number') {
      return '';
    }

    return entities(state).get(entityId)?.name || '';
  }

  return createCachedSelector(
    (state: ReduxState, matchId: number) => {
      const match = matches(state).get(matchId);

      if (!match) {
        return null;
      }

      return `${match.sportId}.${match.tournamentUnionId}.${match.tournamentId}.${match.tournamentStageId}`;
    },
    (state, matchId) => matches(state).get(matchId)?.slotType,
    (state, matchId) => getEntityName(state, matchId, sports, 'sportId'),
    (state, matchId) => getEntityName(state, matchId, tournamentUnions, 'tournamentUnionId'),
    (state, matchId) => getEntityName(state, matchId, tournaments, 'tournamentId'),
    (state, matchId) => getEntityName(state, matchId, tournamentStages, 'tournamentStageId'),
    (matchIds, slotType, sportName, unionName, tournamentName, stageName) => {
      if (!matchIds) {
        return [];
      }

      const [sportId, tournamentUnionId, tournamentId, tournamentStageId] = matchIds.split('.');
      const pathname = slotType ? getPrefixBySlotType(slotType) : null;

      return [
        {
          name: sportName,
          type: BreadcrumbType.SPORT,
          pathname,
          sportId,
        },
        {
          name: unionName,
          type: BreadcrumbType.UNION,
          pathname,
          sportId,
          tournamentUnionId,
        },
        {
          name: tournamentName,
          type: BreadcrumbType.TOURNAMENT,
          pathname,
          sportId,
          tournamentUnionId,
          tournamentId,
        },
        {
          name: stageName,
          type: BreadcrumbType.STAGE,
          pathname,
          sportId,
          tournamentUnionId,
          tournamentId,
          tournamentStageId,
        },
      ];
    },
  )((_state: ReduxState, matchId: number) => matchId);
};
